'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '../../../lib/utils'
import Icon, { iconTypes } from '../Icon'
import { Avatar, AvatarFallback, AvatarImage, AvatarProps } from '../Avatar'
import Link from 'next/link'

import s from './Button.module.css'

import { useRouter } from 'next/navigation'
import { Switch } from '../Switch'

const buttonVariants = cva(
  'inline-flex gap-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 leading-none',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90 ',
        destructive:
          'border-destructive border text-destructive shadow-sm hover:bg-destructive/90 hover:text-destructive/90',
        outline:
          'border border-input bg-background text-foreground shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90',
        sideMenu: 'bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        inset: 'bg-accent hover:bg-accent/50 text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        skeleton: 'bg-secondary animate-pulse w-12',
      },
      size: {
        default: 'h-7 px-4 py-2 text-xs font-normal ',
        sm: 'h-7 rounded-md px-2 text-xs font-normal ease-in ',
        lg: 'h-10 rounded-md px-4 gap-4',
        xl: 'h-16 rounded-md px-5 ',
        icon: 'h-9 w-9',
        secondaryAction: 'px-2 py-1.5 text-xs',
        action: 'h-12 w-full px-4',
      },
      contentPosition: {
        left: 'justify-start',
        right: 'justify-end',
        center: 'justify-center',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      'value' | 'defaultValue'
    >,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  labelClassName?: string
  leftIcon?: iconTypes
  leftIconClassName?: string
  leftIconSize?: number
  rightIcon?: iconTypes
  label?: string
  isLoading?: boolean
  isDisabled?: boolean
  enabledWhileLoading?: boolean
  href?: string
  handleClick?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
  ) => void | Promise<void>
  avatar?: AvatarProps
  showAvatar?: boolean
  contentPosition?: 'left' | 'right' | 'center'
  style?: React.CSSProperties
  asText?: boolean
  value?: string | null
  defaultValue?: string | null
  isSkeleton?: boolean
  isSwitched?: boolean
  description?: string
  alert?: {
    id: string
    label?: string
    className?: string
  }
  truncate?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      leftIconSize = 15,
      rightIcon,
      label,
      isLoading,
      isDisabled,
      handleClick,
      labelClassName,
      avatar,
      leftIconClassName,
      href,
      title,
      style,
      contentPosition = 'center',
      enabledWhileLoading,
      asText,
      isSkeleton,
      isSwitched,
      description,
      alert,
      truncate,
      value: _ignoredValue,
      defaultValue: _ignoredDefaultValue,
      ...props
    },
    ref,
  ) => {
    let router
    try {
      router = useRouter()
    } catch (e) {
      // Router not available (e.g. in Storybook)
      router = null
    }
    const Comp = asChild ? Slot : 'button'

    const processClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>,
    ) => {
      e.preventDefault()
      handleClick && handleClick(e)
      if (href && router) {
        router.push(href)
      }
    }

    const contentPositionClass =
      contentPosition == 'center'
        ? 'justify-center'
        : contentPosition == 'left'
          ? 'justify-start'
          : 'justify-end'

    const textPositionClass =
      contentPosition == 'center'
        ? 'text-center'
        : contentPosition == 'left'
          ? 'text-left'
          : 'text-right'

    const contents = (
      <>
        <div
          className={cn('flex items-center gap-1', contentPositionClass, {
            'flex-1 overflow-hidden': truncate,
          })}
          style={style}
        >
          {(leftIcon || isLoading || avatar) && (
            <div className={cn('flex items-center justify-center')}>
              {leftIcon && !isLoading && (
                <Icon
                  name={leftIcon}
                  size={leftIconSize}
                  className={leftIconClassName}
                />
              )}
              {isLoading && (
                <Icon
                  name="LoaderCircle"
                  size={leftIconSize}
                  className="animate-spin"
                />
              )}
              {avatar && !isLoading && (
                <Avatar className="h-6 w-6 rounded">
                  <AvatarImage src={avatar.img} />
                  <AvatarFallback>{avatar.initials}</AvatarFallback>
                </Avatar>
              )}
            </div>
          )}
          {(label || description) && (
            <div
              className={cn('flex flex-col gap-1', textPositionClass, {
                truncate: truncate,
              })}
            >
              {label && (
                <div
                  className={cn(
                    'align-middle leading-inherit',
                    textPositionClass,
                    labelClassName,
                    {
                      truncate: truncate,
                    },
                  )}
                >
                  {label}
                </div>
              )}

              {description && (
                <div className="align-middle leading-inherit text-xs text-gray-500">
                  {description}
                </div>
              )}
            </div>
          )}
        </div>
        {isSwitched !== undefined && (
          <Switch bgSize="small" toggleSize="small" isChecked={isSwitched} />
        )}

        {rightIcon && !isLoading && <Icon size={15} name={rightIcon} />}

        {alert && (
          <div
            className={cn(
              'bg-red-500 rounded-full flex items-center justify-center text-2xs text-white font-bold',
              alert?.className,
            )}
            title={alert?.id}
          >
            {alert?.label}
          </div>
        )}
      </>
    )

    if (isSkeleton) {
      return (
        <div
          title={`action-${title || label}`}
          className={`${cn(
            buttonVariants({
              variant: isSkeleton ? 'skeleton' : variant,
              size,
              className,
              contentPosition,
            }),
          )} flex flex-row items-center pointer-cursor`}
        ></div>
      )
    }

    if (href && typeof href === 'string' && href.trim() !== '') {
      if (isDisabled) {
        return (
          <div
            title={`action-${title || label}`}
            className={`${cn(
              buttonVariants({ variant, size, className, contentPosition }),
              s.transitionSize,
            )} flex flex-row items-center pointer-cursor`}
          >
            {contents}
          </div>
        )
      }

      if (href.includes('http')) {
        return (
          <a
            title={`action-${title || label}`}
            target="_blank"
            href={href}
            className={`${cn(
              buttonVariants({ variant, size, className, contentPosition }),
              s.transitionSize,
            )} flex flex-row items-center justify-between`}
          >
            {contents}
          </a>
        )
      }

      return (
        <Link
          title={`action-${title || label}`}
          href={href}
          onClick={processClick}
          className={`${cn(
            buttonVariants({ variant, size, className, contentPosition }),
            s.transitionSize,
          )} flex flex-row items-center justify-between`}
        >
          {contents}
        </Link>
      )
    }

    if (asText) {
      return (
        <span
          className={`${cn(
            buttonVariants({ variant, size, className, contentPosition }),
            s.transitionSize,
          )} flex flex-row items-center pointer-cursor cursor-pointer`}
          onClick={processClick}
        >
          {contents}
        </span>
      )
    }

    return (
      <Comp
        title={`action-${title || label}`}
        className={`${cn(
          buttonVariants({ variant, size, className, contentPosition }),
          s.transitionSize,
        )}`}
        ref={ref}
        disabled={enabledWhileLoading ? isDisabled : isDisabled || isLoading}
        onClick={processClick}
      >
        {contents}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
